import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useCurrentUser } from "../lib/useCurrentUser";
import { Card, CardTitle, StatCard } from "../components/ui/Card";
import { Badge, urgencyTone } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import type { Alert, DashboardSummary } from "../lib/types";

interface DashboardData {
  summary: DashboardSummary;
  recent_alerts: Alert[];
}

export default function Dashboard() {
  const api = useApi();
  const { user } = useCurrentUser();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .get<DashboardData>("/api/dashboard")
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="container-page py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-white">
            Here's what your community should know right now.
          </h1>
          <p className="mt-2 text-slate-300">
            Reports are reviewed before they become public alerts. Anonymous
            reporting is supported.
          </p>
        </div>
        {user && (
          <div className="text-right">
            <div className="text-sm text-slate-400">Signed in as</div>
            <div className="text-base text-white">
              {user.full_name ?? user.email}
            </div>
            <div className="mt-1 text-xs uppercase tracking-wider text-accent">
              {user.role.replace("_", " ")}
            </div>
          </div>
        )}
      </header>

      {error && (
        <div className="mt-6 rounded-md border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          Could not load dashboard: {error}
        </div>
      )}

      <section className="mt-8 grid gap-4 grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Active alerts"
          value={loading ? "…" : (data?.summary.active_alerts ?? "—")}
          hint="Reviewed and approved"
        />
        <StatCard
          label="Awaiting verification"
          value={loading ? "…" : (data?.summary.awaiting_verification ?? "—")}
          hint="In verifier queue"
        />
        <StatCard
          label="My reports"
          value={loading ? "…" : (data?.summary.my_reports ?? "—")}
          hint="Across all statuses"
        />
        <StatCard
          label="Trust score"
          value={loading ? "…" : (data?.summary.trust_score ?? "—")}
          hint="Placeholder · roadmap"
        />
      </section>

      <section className="mt-10 grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <CardTitle>Recent approved alerts</CardTitle>
            <Link to="/alerts" className="text-sm text-accent hover:underline">
              View all
            </Link>
          </div>
          {loading && (
            <div className="mt-4 flex items-center gap-3 text-sm text-slate-400">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-accent" />
              Loading recent alerts…
            </div>
          )}
          {!loading && (
            <ul className="mt-4 divide-y divide-white/5">
              {(data?.recent_alerts ?? []).map((a) => (
                <li key={a.id} className="py-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={urgencyTone(a.urgency)}>{a.urgency}</Badge>
                    <Badge>{a.category.replace(/_/g, " ")}</Badge>
                    {a.neighborhood && <Badge>{a.neighborhood}</Badge>}
                  </div>
                  <h4 className="mt-2 text-base font-semibold text-white">
                    {a.title}
                  </h4>
                  <p className="mt-1 text-sm text-slate-300">
                    {a.public_summary}
                  </p>
                  {a.recommended_action && (
                    <p className="mt-1 text-sm text-slate-400">
                      <span className="text-slate-300">Recommended: </span>
                      {a.recommended_action}
                    </p>
                  )}
                </li>
              ))}
              {data && data.recent_alerts.length === 0 && (
                <li className="py-6 text-sm text-slate-400">
                  No active alerts. Community is quiet — that's a good sign.
                </li>
              )}
            </ul>
          )}
        </Card>

        <Card>
          <CardTitle>Quick actions</CardTitle>
          <div className="mt-4 grid gap-3">
            <Link to="/submit-report">
              <Button className="w-full justify-start" variant="primary">
                Submit a report
              </Button>
            </Link>
            <Link to="/my-reports">
              <Button className="w-full justify-start" variant="secondary">
                My reports
              </Button>
            </Link>
            <Link to="/alerts">
              <Button className="w-full justify-start" variant="secondary">
                View alerts
              </Button>
            </Link>
            <Link to="/resources">
              <Button className="w-full justify-start" variant="secondary">
                View resources
              </Button>
            </Link>
            <Link to="/organizations">
              <Button className="w-full justify-start" variant="secondary">
                Contact admin / org
              </Button>
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate-400">
            Community safety starts with trusted information.
          </p>
        </Card>
      </section>
    </div>
  );
}
